import React from 'react';
import { Box, Typography } from '@mui/material';

function normalizeHexColor(input, fallback = '#172983') {
    const value = String(input || '').trim();
    if (/^#[0-9a-fA-F]{6}$/.test(value)) return value;
    if (/^#[0-9a-fA-F]{3}$/.test(value)) {
        return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`;
    }
    return fallback;
}

function hexToRgba(hexColor, alpha) {
    const normalized = normalizeHexColor(hexColor);
    const channels = normalized.match(/[0-9a-fA-F]{2}/g);

    if (!channels) {
        return `rgba(23, 41, 131, ${alpha})`;
    }

    const [r, g, b] = channels.map((channel) => parseInt(channel, 16));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function normalizeMessageText(input) {
    let value = String(input ?? '');

    value = value
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/__([^_]+)__/g, '$1')
        .replace(/\*\*/g, '')
        .replace(/__/g, '');

    return value;
}

function formatMessageTime(dateInput) {
    if (!dateInput) return '';

    const parsedDate = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (Number.isNaN(parsedDate.getTime())) return '';

    try {
        return new Intl.DateTimeFormat(undefined, {
            hour: '2-digit',
            minute: '2-digit',
        }).format(parsedDate);
    } catch (_error) {
        const hours = String(parsedDate.getHours()).padStart(2, '0');
        const minutes = String(parsedDate.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
    }
}

function getShellSx(position) {
    const isUser = position === 'right';

    return {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        gap: 0.8,
    };
}

function getBubbleSx({ position, primaryColor, isMedia = false }) {
    const isUser = position === 'right';
    const resolvedPrimary = normalizeHexColor(primaryColor);

    return {
        width: 'fit-content',
        maxWidth: { xs: '84%', sm: '76%' },
        minWidth: isMedia ? 140 : 96,
        alignSelf: isUser ? 'flex-end' : 'flex-start',
        borderRadius: 4,
        overflow: 'hidden',
        border: isUser
            ? `1px solid ${hexToRgba(resolvedPrimary, 0.9)}`
            : '1px solid rgba(148, 163, 184, 0.26)',
        background: isUser
            ? `linear-gradient(135deg, ${resolvedPrimary} 0%, ${resolvedPrimary} 100%)`
            : 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.96) 100%)',
        color: isUser ? '#ffffff' : '#0f172a',
        boxShadow: isUser
            ? `0 12px 26px ${hexToRgba(resolvedPrimary, 0.22)}`
            : '0 10px 24px rgba(15, 23, 42, 0.08)',
    };
}

function MessageBubble({ position, title, text, timeLabel, primaryColor, children, isMedia = false }) {
    const isUser = position === 'right';
    const normalizedText = normalizeMessageText(text);

    return (
        <Box className={`awn-chat-message-shell awn-chat-message-shell-${position}`} sx={getShellSx(position)}>
            <Box sx={getBubbleSx({ position, primaryColor, isMedia })}>
                <Box sx={{ px: isMedia ? 1 : 1.5, pt: isMedia ? 1 : 1.15, pb: isMedia ? 0.9 : 0.95 }}>
                    {(title || timeLabel) && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                mb: 0.55,
                            }}
                        >
                            <Typography
                                component='div'
                                sx={{
                                    fontSize: '0.74rem',
                                    fontWeight: 700,
                                    letterSpacing: '0.01em',
                                    color: isUser ? 'rgba(255,255,255,0.82)' : '#64748b',
                                    lineHeight: 1.2,
                                }}
                            >
                                {title}
                            </Typography>
                            {timeLabel && (
                                <Typography
                                    component='div'
                                    sx={{
                                        fontSize: '0.72rem',
                                        color: isUser ? 'rgba(255,255,255,0.74)' : '#94a3b8',
                                        lineHeight: 1.2,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {timeLabel}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {typeof normalizedText === 'string' && normalizedText.trim() !== '' && (
                        <Typography
                            component='div'
                            sx={{
                                fontSize: '0.97rem',
                                lineHeight: 1.6,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                color: isUser ? '#ffffff' : '#0f172a',
                                textAlign: 'left',
                            }}
                        >
                            {normalizedText}
                        </Typography>
                    )}

                    {children}
                </Box>
            </Box>
        </Box>
    );
}

function renderMediaEntry({ mediaEntry, index, position, title, timeLabel, primaryColor, onOpen }) {
    const widthToShow = '100%';
    const heightToShow = Number(mediaEntry?.height) > 0 ? `${mediaEntry.height}px` : '240px';

    if (mediaEntry?.type === 'image') {
        return (
            <MessageBubble
                key={`image-${index}`}
                position={position}
                title={title}
                timeLabel={timeLabel}
                primaryColor={primaryColor}
                isMedia
            >
                <>
                    <Box
                        component='img'
                        src={mediaEntry.url}
                        alt={mediaEntry.alt || mediaEntry.name || 'Image'}
                        onClick={onOpen}
                        sx={{
                            display: 'block',
                            width: widthToShow,
                            height: heightToShow,
                            maxHeight: '360px',
                            objectFit: 'contain',
                            borderRadius: 3,
                            cursor: onOpen ? 'zoom-in' : 'default',
                            backgroundColor: '#ffffff',
                        }}
                    />
                    {(mediaEntry.name || mediaEntry.pageNumber) && (
                        <Typography sx={{ mt: 0.75, px: 0.25, fontSize: '0.75rem', color: '#64748b' }}>
                            {mediaEntry.name || 'Document'}{mediaEntry.pageNumber ? ` · Page ${mediaEntry.pageNumber}` : ''}
                        </Typography>
                    )}
                </>
            </MessageBubble>
        );
    }

    if (mediaEntry?.type === 'video') {
        return (
            <MessageBubble
                key={`video-${index}`}
                position={position}
                title={title}
                timeLabel={timeLabel}
                primaryColor={primaryColor}
                isMedia
            >
                <Box
                    component='video'
                    src={mediaEntry.url}
                    controls
                    preload='metadata'
                    sx={{
                        display: 'block',
                        width: widthToShow,
                        height: heightToShow,
                        maxHeight: '360px',
                        objectFit: 'cover',
                        borderRadius: 3,
                        backgroundColor: '#000000',
                    }}
                />
            </MessageBubble>
        );
    }

    if (mediaEntry?.description) {
        return (
            <MessageBubble
                key={`description-${index}`}
                position={position}
                title={title}
                text={mediaEntry.description}
                timeLabel={timeLabel}
                primaryColor={primaryColor}
            />
        );
    }

    return null;
}

export function WrappedRCEMessageBox(props) {
    const messageDate = props.date ? new Date(props.date) : null;
    const timeLabel = formatMessageTime(messageDate);
    const normalizedText = normalizeMessageText(props.text);
    const hasText = typeof normalizedText === 'string' && normalizedText.trim() !== '';

    return (
        <>
            {hasText && (
                <MessageBubble
                    position={props.position}
                    title={props.title}
                    text={normalizedText}
                    timeLabel={timeLabel}
                    primaryColor={props.primaryColor}
                />
            )}

            {Array.isArray(props.media) && props.media.map((mediaEntry, index) => renderMediaEntry({
                mediaEntry,
                index,
                position: props.position,
                title: props.title,
                timeLabel,
                primaryColor: props.primaryColor,
                onOpen: mediaEntry?.type === 'image'
                    ? () => props.handleImageClick && props.handleImageClick(mediaEntry.url)
                    : undefined,
            }))}
        </>
    );
}
